'use client';

import {useParams} from 'next/navigation';
import {AnalyticsEntryPoint, detailAnalytics} from '../analytics';
import {HeroGallery} from '../components/gallery';
import {
  DetailAbout,
  DetailAmenities,
  DetailMasterplanSection,
  DetailNearby,
  DetailNewsCarousel,
  DetailPageModals,
  DetailPhasesCarousel,
  DetailSummary,
  DetailUnitTypesCarousel,
  toMediaAssets,
  useDetailModals,
} from '../components/properties';
import {PageRegisterInterestContext} from '../components/register-interest/PageRegisterInterestContext';
import {openRegisterInterest} from '../components/register-interest/register-interest-controller';
import type {NewsCardView, PhaseCardView, ProjectDetailView, UnitTypeCardView} from '../model';
import {phasesHref, unitTypesHref} from '../routing';

export interface ProjectDetailPageProps {
  project: ProjectDetailView;
  phases: PhaseCardView[];
  unitTypes: UnitTypeCardView[];
  latestNews: NewsCardView[];
}

const ENTRY_POINT = AnalyticsEntryPoint.ProjectDetailsPage;

export function ProjectDetailPage({project, phases, unitTypes, latestNews}: ProjectDetailPageProps) {
  const {locale} = useParams<{locale: string}>();
  const analytics = detailAnalytics(ENTRY_POINT);
  const tracked = {id: project.uuid, name: project.name};
  const modals = useDetailModals(project);
  const onUnitRegisterInterest = (unit: UnitTypeCardView) => {
    analytics.registerInterest({id: unit.uuid, name: unit.title});
    openRegisterInterest({projectId: project.uuid, designTypeId: unit.uuid});
  };
  const onPhaseRegisterInterest = (phase: PhaseCardView) => {
    analytics.registerInterest({id: phase.uuid, name: phase.title});
    openRegisterInterest({projectId: project.uuid, phaseId: phase.uuid});
  };

  return (
    <>
      <PageRegisterInterestContext projectId={project.uuid} />
      <HeroGallery
        assets={toMediaAssets(project.gallery)}
        title={project.name}
        onOpen={() => {
          analytics.gallery(tracked);
        }}
      />
      <DetailSummary item={project} analytics={analytics} registerInterestProjectId={project.uuid} />
      <DetailAbout item={project} analytics={analytics} />
      <DetailUnitTypesCarousel
        units={unitTypes}
        analytics={analytics}
        parentTracked={tracked}
        onRegisterInterest={onUnitRegisterInterest}
        viewAllHref={unitTypesHref(locale, {projectId: project.uuid})}
      />
      <DetailAmenities item={project} analytics={analytics} modals={modals} />
      <DetailMasterplanSection
        media={project.masterplan}
        onView={() => {
          analytics.masterplan(tracked);
        }}
      />
      <DetailPhasesCarousel
        phases={phases}
        analytics={analytics}
        parentTracked={tracked}
        onRegisterInterest={onPhaseRegisterInterest}
        viewAllHref={phasesHref(locale, project.uuid)}
      />
      <DetailNearby item={project} analytics={analytics} modals={modals} />
      <DetailNewsCarousel articles={latestNews} analytics={analytics} parentTracked={tracked} viewAllProjectId={project.uuid} />
      <DetailPageModals amenities={project.amenities} modals={modals} />
    </>
  );
}

ProjectDetailPage.displayName = 'ProjectDetailPage';
